// Intervals — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/greedy.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the sweep.

const intervalProblems = [
  {
    id: 'insert-interval',
    title: 'Insert Interval',
    difficulty: 'Medium',
    leetcode: { number: 57, url: 'https://leetcode.com/problems/insert-interval/' },
    statement: 'Given a list of non-overlapping intervals sorted by start time, insert a new interval into the list, merging any overlaps so the result stays sorted and non-overlapping.',
    example: {
      input: 'intervals = [[1,3],[6,9]], newInterval = [2,5]',
      output: '[[1,5],[6,9]]',
      note: '[2,5] overlaps [1,3] (since 2 ≤ 3), merging into [1,5]. [6,9] stays untouched.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Add and Re-Merge Everything)',
        why:
          "Simply append the new interval to the existing list, sort the whole thing by start time, and run " +
          "the standard Merge Intervals routine on it. Correct, but resorting and re-merging the ENTIRE list " +
          "throws away the fact that it was already sorted and non-overlapping before the insertion.",
        steps: [
          'Add newInterval to the existing list of intervals.',
          'Sort the combined list by start time.',
          'Run the standard merge routine: scan once, merging any interval that overlaps the last one kept.',
          'Return the merged result.',
        ],
        code:
`int[][] insertBruteForce(int[][] intervals, int[] newInterval) {
    List<int[]> all = new ArrayList<>(Arrays.asList(intervals));
    all.add(newInterval);
    all.sort((a, b) -> a[0] - b[0]);

    List<int[]> merged = new ArrayList<>();
    for (int[] iv : all) {
        if (!merged.isEmpty() && iv[0] <= merged.get(merged.size() - 1)[1]) {
            merged.get(merged.size() - 1)[1] = Math.max(merged.get(merged.size() - 1)[1], iv[1]);
        } else {
            merged.add(iv);
        }
    }
    return merged.toArray(new int[0][]);
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        whenToUse: "Correct, but wastes the fact that the input was already sorted and non-overlapping — the optimized approach exploits that guarantee directly, in linear time.",
      },
      {
        name: 'Approach 2 — Optimized: Single Pass, Three Phases',
        why:
          "Since the input is already sorted and non-overlapping, no sorting is ever needed. A single pass " +
          "naturally splits into three phases: intervals entirely before the new one, intervals that overlap " +
          "and must be merged into it, and intervals entirely after.",
        steps: [
          'Add every interval that ends BEFORE the new interval starts, unchanged.',
          'While an interval overlaps the (possibly still-growing) new interval, merge it in — expanding the new interval\'s start and end as needed.',
          'Add the now-finalized, fully-merged new interval.',
          'Add every remaining interval, unchanged.',
        ],
        visual: {
          arrays: [{ label: 'intervals', values: ['[1,3]', '[6,9]'] }],
          varNames: ['newInterval'],
          steps: [
            { vars: { newInterval: '[2,5]' }, note: 'Start with newInterval = [2,5].' },
            { highlights: { intervals: [0] }, vars: { newInterval: '[1,5]' }, note: '[1,3] overlaps [2,5] (1 ≤ 5 and 2 ≤ 3) — merge: newInterval becomes [min(1,2), max(3,5)] = [1,5].' },
            { highlights: { intervals: [1] }, vars: { newInterval: '[1,5]' }, note: '[6,9] starts after newInterval ends (6 > 5) — no more merging. Add [1,5], then add [6,9] unchanged.' },
            { note: 'Final result: [[1,5],[6,9]].' },
          ],
        },
        code:
`int[][] insert(int[][] intervals, int[] newInterval) {
    List<int[]> result = new ArrayList<>();
    int i = 0, n = intervals.length;

    while (i < n && intervals[i][1] < newInterval[0]) {
        result.add(intervals[i++]);
    }
    while (i < n && intervals[i][0] <= newInterval[1]) {
        newInterval[0] = Math.min(newInterval[0], intervals[i][0]);
        newInterval[1] = Math.max(newInterval[1], intervals[i][1]);
        i++;
    }
    result.add(newInterval);
    while (i < n) {
        result.add(intervals[i++]);
    }
    return result.toArray(new int[0][]);
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
        interviewNote: "Try the \"Insert Interval\" mode in this pattern's interactive playground to watch the three phases play out on any input.",
        whenToUse: "The standard expected solution — recognizing the already-sorted, already-non-overlapping input is the key to skipping a full re-sort entirely.",
      },
    ],
  },
  {
    id: 'merge-intervals',
    title: 'Merge Intervals',
    difficulty: 'Medium',
    leetcode: { number: 56, url: 'https://leetcode.com/problems/merge-intervals/' },
    statement: 'Given an array of intervals, merge all overlapping ones and return an array of the non-overlapping intervals that cover exactly the same total range.',
    example: {
      input: 'intervals = [[1,3],[2,6],[8,10],[15,18]]',
      output: '[[1,6],[8,10],[15,18]]',
      note: '[1,3] and [2,6] overlap (2 ≤ 3), merging into [1,6]. The rest don\'t overlap anything.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Repeated Pairwise Merging)',
        why:
          "Repeatedly scan the whole list looking for ANY two intervals that overlap, merge them, and start " +
          "over — continuing until a full pass finds nothing left to merge. Correct, but a single merge can " +
          "require re-scanning the entire list many times before convergence.",
        steps: [
          'Scan every pair of intervals in the list.',
          'The moment an overlapping pair is found, merge them into one and restart the scan from the beginning.',
          'Repeat until a complete pass finds no overlapping pair left.',
          'The final list is the answer.',
        ],
        code:
`List<int[]> mergeBruteForce(List<int[]> intervals) {
    boolean merged = true;
    while (merged) {
        merged = false;
        outer:
        for (int i = 0; i < intervals.size(); i++) {
            for (int j = i + 1; j < intervals.size(); j++) {
                int[] a = intervals.get(i), b = intervals.get(j);
                if (a[0] <= b[1] && b[0] <= a[1]) {   // overlap check
                    int[] combined = { Math.min(a[0], b[0]), Math.max(a[1], b[1]) };
                    intervals.remove(j); intervals.remove(i);
                    intervals.add(combined);
                    merged = true;
                    break outer;
                }
            }
        }
    }
    return intervals;
}`,
        complexity: { time: 'O(n³) worst case', space: 'O(n)' },
        whenToUse: "Only useful to confirm what \"merge\" means pairwise — restarting the scan after every merge is what makes this quadratic-to-cubic instead of linearithmic.",
      },
      {
        name: 'Approach 2 — Optimized: Sort by Start, One Pass',
        why:
          "Sorting by start time first guarantees that any interval overlapping the current one must be " +
          "immediately adjacent in the sorted order — no re-scanning is ever needed, since two intervals " +
          "separated by a non-overlapping one in between can't possibly overlap each other either.",
        steps: [
          'Sort all intervals by their start value.',
          'Walk through once, keeping a "last merged interval" reference.',
          'If the current interval\'s start is ≤ the last merged interval\'s end, they overlap — extend the last merged interval\'s end if needed.',
          'Otherwise, the current interval starts a new, separate merged group.',
        ],
        visual: {
          arrays: [{ label: 'intervals (sorted by start)', values: ['[1,3]', '[2,6]', '[8,10]', '[15,18]'] }],
          varNames: [],
          steps: [
            { highlights: { 'intervals (sorted by start)': [0] }, note: '[1,3]: first interval — start the merged list with it.' },
            { highlights: { 'intervals (sorted by start)': [1] }, note: '[2,6]: start (2) ≤ last merged end (3) — overlaps! Extend to [1,6].' },
            { highlights: { 'intervals (sorted by start)': [2] }, note: '[8,10]: start (8) > last merged end (6) — no overlap. Start a new merged interval.' },
            { highlights: { 'intervals (sorted by start)': [3] }, note: '[15,18]: start (15) > last merged end (10) — no overlap. Start another new merged interval. Final: [[1,6],[8,10],[15,18]].' },
          ],
        },
        code:
`int[][] merge(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[0] - b[0]);
    List<int[]> merged = new ArrayList<>();
    for (int[] iv : intervals) {
        if (!merged.isEmpty() && iv[0] <= merged.get(merged.size() - 1)[1]) {
            merged.get(merged.size() - 1)[1] = Math.max(merged.get(merged.size() - 1)[1], iv[1]);
        } else {
            merged.add(iv);
        }
    }
    return merged.toArray(new int[0][]);
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        interviewNote: "Try the \"Merge Intervals\" mode in this pattern's interactive playground to watch the sorted single pass merge any input.",
        whenToUse: "The canonical interval-merging solution, and the direct foundation Insert Interval, Employee Free Time, and My Calendar all build on — sort by start, then one linear pass.",
      },
    ],
  },
  {
    id: 'non-overlapping-intervals',
    title: 'Non-overlapping Intervals',
    difficulty: 'Medium',
    leetcode: { number: 435, url: 'https://leetcode.com/problems/non-overlapping-intervals/' },
    statement: 'Given an array of intervals, find the minimum number of intervals that must be removed so that the rest don\'t overlap each other.',
    example: {
      input: 'intervals = [[1,2],[2,3],[3,4],[1,3]]',
      output: '1',
      note: 'Removing [1,3] leaves [1,2],[2,3],[3,4] — none overlap (touching at an endpoint is allowed).',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Try Every Subset)',
        why:
          "Try every possible subset of intervals, keep only the subsets where no two intervals overlap, and " +
          "find the LARGEST such subset — the answer is the total count minus that largest subset\'s size. " +
          "Correct, but the number of subsets is exponential.",
        steps: [
          'Generate every subset of the interval list.',
          'For each subset, check whether every pair of intervals inside it is non-overlapping.',
          'Track the largest valid (fully non-overlapping) subset found.',
          'The answer is (total intervals) minus (the largest valid subset\'s size).',
        ],
        code:
`int eraseOverlapIntervalsBruteForce(int[][] intervals) {
    int n = intervals.length;
    int maxKept = 0;
    for (int mask = 0; mask < (1 << n); mask++) {
        List<int[]> subset = new ArrayList<>();
        for (int i = 0; i < n; i++) if ((mask & (1 << i)) != 0) subset.add(intervals[i]);
        if (isNonOverlapping(subset)) {
            maxKept = Math.max(maxKept, subset.size());
        }
    }
    return n - maxKept;
}

private boolean isNonOverlapping(List<int[]> subset) {
    for (int i = 0; i < subset.size(); i++) {
        for (int j = i + 1; j < subset.size(); j++) {
            int[] a = subset.get(i), b = subset.get(j);
            if (a[0] < b[1] && b[0] < a[1]) return false;
        }
    }
    return true;
}`,
        complexity: { time: 'O(2ⁿ · n²)', space: 'O(n)' },
        whenToUse: "Only useful to confirm what \"non-overlapping\" means for a subset — exponential subset enumeration is impractical beyond tiny inputs.",
      },
      {
        name: 'Approach 2 — Optimized: Greedy, Sort by END',
        why:
          "This is the classic \"activity selection\" greedy: sorting by END time (not start!) and always keeping " +
          "an interval whenever it doesn\'t conflict with the last KEPT one maximizes the number of intervals kept " +
          "— an interval that ends earliest leaves the most room for everything that comes after it.",
        steps: [
          'Sort intervals by their END value.',
          'Track the end of the last KEPT interval, starting at negative infinity.',
          'For each interval: if its start is ≥ the last kept end, KEEP it (no conflict) and update the last kept end.',
          'Otherwise, it conflicts — REMOVE it (count it), and leave the last kept end unchanged.',
        ],
        visual: {
          arrays: [{ label: 'intervals (sorted by end)', values: ['[1,2]', '[2,3]', '[1,3]', '[3,4]'] }],
          varNames: ['removed'],
          steps: [
            { highlights: { 'intervals (sorted by end)': [0] }, vars: { removed: 0 }, note: '[1,2]: start(1) ≥ lastEnd(−∞) — KEEP. lastEnd becomes 2.' },
            { highlights: { 'intervals (sorted by end)': [1] }, vars: { removed: 0 }, note: '[2,3]: start(2) ≥ lastEnd(2) — KEEP (touching is fine). lastEnd becomes 3.' },
            { highlights: { 'intervals (sorted by end)': [2] }, vars: { removed: 1 }, note: '[1,3]: start(1) < lastEnd(3) — overlaps! REMOVE it. removed=1. lastEnd stays 3.' },
            { highlights: { 'intervals (sorted by end)': [3] }, vars: { removed: 1 }, note: '[3,4]: start(3) ≥ lastEnd(3) — KEEP. Final answer: 1 interval removed.' },
          ],
        },
        code:
`int eraseOverlapIntervals(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[1] - b[1]);
    int removed = 0;
    int lastEnd = Integer.MIN_VALUE;
    for (int[] iv : intervals) {
        if (iv[0] >= lastEnd) {
            lastEnd = iv[1];
        } else {
            removed++;
        }
    }
    return removed;
}`,
        complexity: { time: 'O(n log n)', space: 'O(1) extra' },
        interviewNote: "Try the \"Non-overlapping\" mode in this pattern's interactive playground — green bars are kept, red bars are removed.",
        whenToUse: "The classic activity-selection greedy — sorting by END (never by start) is the detail that makes this provably optimal; it's a direct application of the exchange argument covered in the Greedy pattern.",
      },
    ],
  },
  {
    id: 'meeting-rooms',
    title: 'Meeting Rooms',
    difficulty: 'Easy',
    leetcode: { number: 252, url: 'https://leetcode.com/problems/meeting-rooms/' },
    statement: 'Given an array of meeting time intervals, determine if a person could attend every single meeting (i.e., no two meetings overlap).',
    example: {
      input: 'intervals = [[0,30],[5,10],[15,20]]',
      output: 'false',
      note: '[0,30] overlaps both [5,10] and [15,20] — impossible to attend all three.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Every Pair)',
        why:
          "Check every pair of meetings for an overlap directly. Correct and simple, but checks many pairs " +
          "that are obviously far apart and could never overlap.",
        steps: [
          'For every pair of meetings, check whether they overlap: start1 < end2 AND start2 < end1.',
          'If any pair overlaps, it\'s impossible to attend both — return false.',
          'If no pair overlaps after checking all of them, return true.',
        ],
        code:
`boolean canAttendMeetingsBruteForce(int[][] intervals) {
    for (int i = 0; i < intervals.length; i++) {
        for (int j = i + 1; j < intervals.length; j++) {
            if (intervals[i][0] < intervals[j][1] && intervals[j][0] < intervals[i][1]) {
                return false;
            }
        }
    }
    return true;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
        whenToUse: "Fine for small inputs, but the sorted version below finds a conflict without ever needing to compare distant, obviously-non-overlapping pairs.",
      },
      {
        name: 'Approach 2 — Optimized: Sort by Start, Check Adjacent Pairs',
        why:
          "Once sorted by start time, TWO meetings can only possibly overlap if they're adjacent in that sorted " +
          "order — if meeting i doesn't overlap meeting i+1, it can't overlap anything even further along either, " +
          "since everything after starts even later.",
        steps: [
          'Sort all meetings by start time.',
          'Walk through adjacent pairs: if the current meeting starts before the previous one ends, they overlap.',
          'Return false the moment any adjacent overlap is found.',
          'If no adjacent pair overlaps, return true.',
        ],
        code:
`boolean canAttendMeetings(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[0] - b[0]);
    for (int i = 1; i < intervals.length; i++) {
        if (intervals[i][0] < intervals[i - 1][1]) {
            return false;
        }
    }
    return true;
}`,
        complexity: { time: 'O(n log n)', space: 'O(1) extra' },
        whenToUse: "The standard warm-up for this pattern — establishing that sorting by start turns an O(n²) all-pairs check into a single O(n) adjacent-pairs scan, a technique Meeting Rooms II builds directly on.",
      },
    ],
  },
  {
    id: 'meeting-rooms-ii',
    title: 'Meeting Rooms II',
    difficulty: 'Medium',
    leetcode: { number: 253, url: 'https://leetcode.com/problems/meeting-rooms-ii/' },
    statement: 'Given an array of meeting time intervals, find the minimum number of conference rooms required to hold all of them.',
    example: {
      input: 'intervals = [[0,30],[5,10],[15,20]]',
      output: '2',
      note: '[5,10] and [15,20] can share a room sequentially, but [0,30] needs its own room the whole time — 2 rooms total.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Simulate Every Minute)',
        why:
          "For every point in time across the whole schedule, count how many meetings are simultaneously active " +
          "— the maximum count found anywhere is the number of rooms needed. Correct, but scans every time unit, " +
          "even ones where nothing changes.",
        steps: [
          'Find the overall time range covered by all meetings.',
          'For each time unit, count how many meetings are active (start ≤ t < end).',
          'Track the maximum count found across the whole range.',
          'Return that maximum.',
        ],
        code:
`int minMeetingRoomsBruteForce(int[][] intervals) {
    int maxTime = 0;
    for (int[] iv : intervals) maxTime = Math.max(maxTime, iv[1]);
    int maxRooms = 0;
    for (int t = 0; t < maxTime; t++) {
        int active = 0;
        for (int[] iv : intervals) {
            if (t >= iv[0] && t < iv[1]) active++;
        }
        maxRooms = Math.max(maxRooms, active);
    }
    return maxRooms;
}`,
        complexity: { time: 'O(maxTime · n)', space: 'O(1) extra' },
        whenToUse: "Only practical for small time ranges — scanning every individual minute is wasteful when only the moments meetings START or END actually matter.",
      },
      {
        name: 'Approach 2 — Optimized: Min-Heap of End Times',
        why:
          "Sort meetings by start time, and track ongoing meetings\' end times in a min-heap. For each new " +
          "meeting, check whether the EARLIEST-ending ongoing meeting has already finished — if so, that room " +
          "can be reused; otherwise, a brand new room is required.",
        steps: [
          'Sort meetings by start time.',
          'For each meeting, check the min-heap\'s smallest end time.',
          'If that smallest end time is ≤ this meeting\'s start, a room just freed up — pop it and push this meeting\'s end (reuse).',
          'Otherwise, no room is free — push this meeting\'s end anyway (allocate a new room). The final heap size is the rooms needed.',
        ],
        code:
`int minMeetingRoomsHeap(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[0] - b[0]);
    PriorityQueue<Integer> endTimes = new PriorityQueue<>();
    for (int[] iv : intervals) {
        if (!endTimes.isEmpty() && endTimes.peek() <= iv[0]) {
            endTimes.poll();
        }
        endTimes.add(iv[1]);
    }
    return endTimes.size();
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        whenToUse: "A very natural way to think about the problem — literally simulating which room becomes free first — and a great answer if a min-heap comes to mind first in an interview.",
      },
      {
        name: 'Approach 3 — Further Optimized: Two-Pointer Chronological Sweep',
        why:
          "Separate ALL start times and ALL end times into their own sorted arrays. Then sweep through " +
          "chronologically: every time a meeting starts before the earliest currently-unprocessed end, a NEW " +
          "room is needed; every time an end is reached before the next start, a room frees up. No heap needed " +
          "at all — just two sorted arrays and two pointers.",
        steps: [
          'Extract all start times into one array, and all end times into another; sort both independently.',
          'Walk both with pointers s (into starts) and e (into ends), tracking rooms currently in use.',
          'If the next start comes before the next end, a new room is needed: increment rooms, advance s, and update the peak.',
          'Otherwise, a room has freed up: decrement rooms, and advance e. Continue until every start has been processed.',
        ],
        visual: {
          arrays: [{ label: 'meetings', values: ['[0,30]', '[5,10]', '[15,20]'] }],
          varNames: ['rooms', 'maxRooms'],
          steps: [
            { vars: { rooms: 0, maxRooms: 0 }, note: 'Sorted starts: [0, 5, 15]. Sorted ends: [10, 20, 30].' },
            { vars: { rooms: 1, maxRooms: 1 }, note: 'start[0]=0 < end[0]=10 — a meeting begins before any end. rooms=1, peak=1.' },
            { vars: { rooms: 2, maxRooms: 2 }, note: 'start[1]=5 < end[0]=10 — another meeting begins before the first ends. rooms=2, peak=2.' },
            { vars: { rooms: 1, maxRooms: 2 }, note: 'start[2]=15 ≥ end[0]=10 — a room frees up first. rooms=1 (peak stays 2).' },
            { vars: { rooms: 2, maxRooms: 2 }, note: 'Now start[2]=15 < end[1]=20 — the third meeting begins. rooms=2. All starts processed — final answer: 2 rooms.' },
          ],
        },
        code:
`int minMeetingRooms(int[][] intervals) {
    int n = intervals.length;
    int[] starts = new int[n], ends = new int[n];
    for (int i = 0; i < n; i++) {
        starts[i] = intervals[i][0];
        ends[i] = intervals[i][1];
    }
    Arrays.sort(starts);
    Arrays.sort(ends);

    int s = 0, e = 0, rooms = 0, maxRooms = 0;
    while (s < n) {
        if (starts[s] < ends[e]) {
            rooms++;
            maxRooms = Math.max(maxRooms, rooms);
            s++;
        } else {
            rooms--;
            e++;
        }
    }
    return maxRooms;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        interviewNote: "Try the \"Meeting Rooms II\" mode in this pattern's interactive playground to watch the two-pointer sweep run on any set of meetings.",
        whenToUse: "The cleanest optimal solution — no heap bookkeeping, just two sorted arrays swept in lockstep. A strong closing answer once the min-heap version has already been discussed.",
      },
    ],
  },
];

export default intervalProblems;
